import React, { useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card, CardHeader, CardContent } from '../components/Card';
import { Badge } from '../components/Badge';
import { BackgroundSlider } from '../components/BackgroundSlider';
import { ArrowRight, Check, User, Brain, MessageSquare, Target, Clock, AlertTriangle, Shield, Zap } from 'lucide-react';
interface DrillingPotPageProps {
  onNavigate?: (path: string) => void;
}
// Declare Paystack on window object
declare global {
  interface Window {
    PaystackPop: any;
  }
}
export function DrillingPotPage({
  onNavigate
}: DrillingPotPageProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [paymentOption, setPaymentOption] = useState<'annual' | 'quarterly' | 'split'>('annual');
  const [showForm, setShowForm] = useState(false);
  // Handle scroll to payment if hash is present
  useEffect(() => {
    if (window.location.hash === '#payment') {
      setTimeout(() => {
        const element = document.getElementById('payment');
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);
  const handlePayment = () => {
    if (!email || !name) {
      alert('Please enter your name and email to proceed with application');
      setShowForm(true);
      return;
    }
    // Calculate amount based on payment option
    let amount: number;
    let paymentDescription: string;
    if (paymentOption === 'annual') {
      amount = 70000000; // ₦700,000 in kobo
      paymentDescription = 'Annual Commitment (Full Payment)';
    } else if (paymentOption === 'quarterly') {
      amount = 35000000; // ₦350,000 in kobo
      paymentDescription = 'Quarterly Commitment (First Payment)';
    } else {
      amount = 35000000; // ₦350,000 in kobo (50% upfront)
      paymentDescription = 'Split Payment (50% Upfront - Balance due Month 2)';
    }
    // Paystack integration
    const handler = window.PaystackPop.setup({
      key: 'pk_live_c2f71547b29d95243a012d9ef2f8e03485d942e7',
      email: email,
      amount: amount,
      currency: 'NGN',
      ref: 'DP_' + Math.floor(Math.random() * 1000000000 + 1),
      metadata: {
        custom_fields: [{
          display_name: 'Full Name',
          variable_name: 'full_name',
          value: name
        }, {
          display_name: 'Program',
          variable_name: 'program',
          value: 'The Drilling Pot (One-on-One)'
        }, {
          display_name: 'Payment Option',
          variable_name: 'payment_option',
          value: paymentOption
        }, {
          display_name: 'Payment Description',
          variable_name: 'payment_description',
          value: paymentDescription
        }]
      },
      callback: function (response: any) {
        // Payment successful - redirect to WhatsApp community
        if (paymentOption === 'split') {
          alert('First payment received! You will be contacted about the second payment (₦350,000) at the beginning of month 2. You will now be redirected to schedule your alignment conversation.');
        } else {
          alert('Application fee received! You will now be redirected to schedule your alignment conversation.');
        }
        window.location.href = 'https://chat.whatsapp.com/CPhnSFkreyVJl4M8AF8zZo';
      },
      onClose: function () {
        // Payment cancelled
        console.log('Payment cancelled');
      }
    });
    handler.openIframe();
  };
  const scrollToPayment = () => {
    const element = document.getElementById('payment');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      setShowForm(true);
    }
  };
  const scrollToDetails = () => {
    const element = document.getElementById('overview');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933] font-sans selection:bg-salon-red/20 selection:text-[#1f2933]" data-id="element-883">
      {/* Load Paystack Script */}
      <script src="https://js.paystack.co/v1/inline.js" data-id="element-884"></script>

      <Navbar onNavigate={onNavigate} data-id="element-885" />

      {/* Background Slider */}
      <BackgroundSlider data-id="element-886" />

      {/* HERO SECTION */}
      <section className="relative border-b border-dashed border-[#9ca3af] py-24 sm:py-32 overflow-hidden" data-id="element-887">
        <div className="absolute inset-0 z-10 bg-[linear-gradient(to_right,#9ca3af20_1px,transparent_1px),linear-gradient(to_bottom,#9ca3af20_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" data-id="element-888" />

        <div className="relative z-20 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-889">
          <Badge variant="secondary" className="mb-8 mx-auto font-mono text-xs tracking-wider border-salon-red text-salon-red bg-salon-red/10" data-id="element-890">
            PRIVATE_APPRENTICESHIP
          </Badge>

          <h1 className="mx-auto max-w-5xl text-5xl font-bold tracking-tight sm:text-7xl mb-4 text-[#1f2933]" data-id="element-891">
            THE DRILLING POT
          </h1>

          <p className="font-mono text-sm text-[#9ca3af] mb-6 tracking-widest uppercase" data-id="element-892">
            A 12-Month Private Apprenticeship
          </p>

          <p className="mx-auto max-w-2xl text-lg font-mono text-[#1f2933]/80 mb-10 leading-relaxed" data-id="element-893">
            In Personal Brand, AI Leverage & Public Presence.
            <br data-id="element-894" />
            <span className="text-salon-red font-bold" data-id="element-895">
              A practice-driven learning relationship built around real output,
              real feedback, and real-world execution.
            </span>
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-896">
            <Button size="large" rightIcon={<ArrowRight className="w-4 h-4" data-id="element-898" />} onClick={scrollToPayment} className="bg-salon-red hover:bg-salon-red/90 border-salon-red text-white" data-id="element-897">
              APPLY_NOW
            </Button>
            <Button variant="secondary" size="large" onClick={scrollToDetails} className="border-[#1f2933] text-[#1f2933] hover:bg-[#1f2933] hover:text-white" data-id="element-899">
              LEARN_MORE
            </Button>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section id="overview" className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-900">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8" data-id="element-901">
          <div className="mb-12" data-id="element-902">
            <h2 className="font-mono text-xs font-bold text-salon-red mb-2" data-id="element-903">
              01_OVERVIEW
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933] mb-6" data-id="element-904">
              Why "The Drilling Pot"?
            </h3>
            <p className="text-lg text-[#1f2933]/80 mb-6 leading-relaxed" data-id="element-905">
              A drilling pot is where pressure, heat, and time work together to
              transform raw material into something useful and durable. This
              program is designed the same way.
            </p>
            <div className="bg-white border-l-4 border-salon-red p-6 mb-8" data-id="element-906">
              <p className="font-mono text-sm text-[#1f2933]" data-id="element-907">
                This is NOT a course.
                <br data-id="element-908" />
                It is NOT motivational coaching.
                <br data-id="element-909" />
                It is NOT recycled theory from YouTube.
              </p>
            </div>
            <p className="text-lg text-[#1f2933]/80 leading-relaxed" data-id="element-910">
              The Drilling Pot is a practice-driven learning relationship built
              around real output, real feedback, and real-world
              execution—without the drag that comes with group programs.
            </p>
          </div>
        </div>
      </section>

      {/* IDEAL PARTICIPANT */}
      <section className="py-24 border-b border-dashed border-[#9ca3af] bg-[#e5e5e7]" data-id="element-911">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-912">
          <div className="flex flex-col md:flex-row gap-16 items-start" data-id="element-913">
            <div className="w-full md:w-1/2" data-id="element-914">
              <h2 className="font-mono text-xs font-bold text-salon-teal mb-2" data-id="element-915">
                02_IDEAL_PARTICIPANT
              </h2>
              <h3 className="text-3xl font-bold text-[#1f2933] mb-6" data-id="element-916">
                Who This Is For
              </h3>
              <p className="font-mono text-sm text-[#1f2933]/70 mb-8" data-id="element-917">
                This program assumes desire, discipline, and honesty—not prior
                expertise.
              </p>

              <ul className="space-y-4" data-id="element-918">
                {['Consider yourself a beginner but serious about mastery', 'Want to build a personal brand with substance, not aesthetics', 'Want to use AI as a thinking partner, not a shortcut', 'Value lived practice over consuming information', 'Willing to ship imperfect work, receive feedback, and iterate', 'Want faster personal velocity than group environments allow'].map((item, i) => <li key={i} className="flex items-start gap-3 border-b border-dashed border-[#9ca3af]/30 pb-2" data-id="element-919">
                    <Check className="w-5 h-5 text-salon-red flex-shrink-0 mt-0.5" data-id="element-920" />
                    <span className="text-[#1f2933] font-medium" data-id="element-921">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="w-full md:w-1/2" data-id="element-922">
              <Card className="bg-[#f5f5f7] border-2 border-[#1f2933]" data-id="element-923">
                <CardHeader title="PROGRAM_PHILOSOPHY" className="font-mono text-xs font-bold text-[#1f2933]" data-id="element-924" />
                <CardContent data-id="element-925">
                  <h4 className="text-xl font-bold mb-4" data-id="element-926">
                    Self-Directed Learning (Heutagogy)
                  </h4>
                  <ul className="space-y-3 mb-6 font-mono text-sm text-[#1f2933]/80" data-id="element-927">
                    <li className="flex gap-2" data-id="element-928">
                      <span className="text-salon-red" data-id="element-929">→</span> You own your
                      goals
                    </li>
                    <li className="flex gap-2" data-id="element-930">
                      <span className="text-salon-red" data-id="element-931">→</span> Learning is
                      driven by action, not lectures
                    </li>
                    <li className="flex gap-2" data-id="element-932">
                      <span className="text-salon-red" data-id="element-933">→</span> Progress is
                      measured by output
                    </li>
                    <li className="flex gap-2" data-id="element-934">
                      <span className="text-salon-red" data-id="element-935">→</span> AI accelerates
                      thinking, doesn't replace it
                    </li>
                    <li className="flex gap-2" data-id="element-936">
                      <span className="text-salon-red" data-id="element-937">→</span> Real work
                      happens in public
                    </li>
                  </ul>
                  <div className="border-t border-dashed border-[#9ca3af] pt-4" data-id="element-938">
                    <p className="italic text-[#1f2933]" data-id="element-939">
                      "You are not taught what to think. You are trained to
                      build, test, refine, and lead yourself over time."
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CURRICULUM AREAS */}
      <section className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-940">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-941">
          <div className="mb-16" data-id="element-942">
            <h2 className="font-mono text-xs font-bold text-salon-red mb-2" data-id="element-943">
              04_CURRICULUM_AREAS
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933]" data-id="element-944">
              Four Integrated Focus Areas
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-945">
            <Card className="border-l-4 border-l-[#1f2933]" data-id="element-946">
              <CardHeader title="1. Personal Brand Foundations" className="font-bold" data-id="element-947" />
              <CardContent data-id="element-948">
                <User className="w-8 h-8 text-[#1f2933] mb-4" data-id="element-949" />
                <ul className="space-y-2 font-mono text-sm text-[#1f2933]/80" data-id="element-950">
                  <li data-id="element-951">
                    • Clarifying your voice, perspective, and credibility lane
                  </li>
                  <li data-id="element-952">• Translating lived experience into public insight</li>
                  <li data-id="element-953">• Positioning without pretending or posturing</li>
                  <li data-id="element-954">• Building identity before visibility</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-salon-red" data-id="element-955">
              <CardHeader title="2. LinkedIn & TikTok as Practice Arenas" className="font-bold" data-id="element-956" />
              <CardContent data-id="element-957">
                <MessageSquare className="w-8 h-8 text-salon-red mb-4" data-id="element-958" />
                <ul className="space-y-2 font-mono text-sm text-[#1f2933]/80" data-id="element-959">
                  <li data-id="element-960">• Writing to think, not to perform</li>
                  <li data-id="element-961">• Developing consistency without burnout</li>
                  <li data-id="element-962">• Learning platforms through use, not study</li>
                  <li data-id="element-963">• Building presence, confidence, and audience trust</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-salon-teal" data-id="element-964">
              <CardHeader title="3. Practical AI Leverage" className="font-bold" data-id="element-965" />
              <CardContent data-id="element-966">
                <Brain className="w-8 h-8 text-salon-teal mb-4" data-id="element-967" />
                <ul className="space-y-2 font-mono text-sm text-[#1f2933]/80" data-id="element-968">
                  <li data-id="element-969">• Using AI to clarify thinking and structure ideas</li>
                  <li data-id="element-970">• Building repeatable workflows for content</li>
                  <li data-id="element-971">• Turning notes and experiences into assets</li>
                  <li data-id="element-972">• Ethical and authentic use of AI in public work</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-salon-yellow" data-id="element-973">
              <CardHeader title="4. Authority, Teaching & Public Presence" className="font-bold" data-id="element-974" />
              <CardContent data-id="element-975">
                <Target className="w-8 h-8 text-salon-yellow mb-4" data-id="element-976" />
                <ul className="space-y-2 font-mono text-sm text-[#1f2933]/80" data-id="element-977">
                  <li data-id="element-978">• Developing a signature idea or point of view</li>
                  <li data-id="element-979">• Learning to speak, teach, and explain clearly</li>
                  <li data-id="element-980">• Moving from posting content to holding attention</li>
                  <li data-id="element-981">
                    • Preparing for speaking or thought-leadership opportunities
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* STRUCTURE & EXPECTATIONS */}
      <section className="py-24 border-b border-dashed border-[#9ca3af] bg-[#1f2933] text-[#f5f5f7]" data-id="element-982">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-983">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16" data-id="element-984">
            <div data-id="element-985">
              <h2 className="font-mono text-xs font-bold text-salon-red mb-2" data-id="element-986">
                05_STRUCTURE
              </h2>
              <h3 className="text-3xl font-bold mb-6" data-id="element-987">
                The Working Relationship
              </h3>
              <ul className="space-y-4" data-id="element-988">
                <li className="flex items-start gap-3" data-id="element-989">
                  <Clock className="w-5 h-5 text-salon-red flex-shrink-0 mt-1" data-id="element-990" />
                  <div data-id="element-991">
                    <strong className="block mb-1" data-id="element-992">
                      Twice-Weekly Live Sessions
                    </strong>
                    <span className="text-[#9ca3af] text-sm font-mono" data-id="element-993">
                      30-minute one-on-one calls (scheduled)
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-994">
                  <Zap className="w-5 h-5 text-salon-red flex-shrink-0 mt-1" data-id="element-995" />
                  <div data-id="element-996">
                    <strong className="block mb-1" data-id="element-997">
                      Direct Thinking Support
                    </strong>
                    <span className="text-[#9ca3af] text-sm font-mono" data-id="element-998">
                      Not generic advice. Continuous guidance and recalibration.
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-999">
                  <Target className="w-5 h-5 text-salon-red flex-shrink-0 mt-1" data-id="element-1000" />
                  <div data-id="element-1001">
                    <strong className="block mb-1" data-id="element-1002">
                      Practical Assignments
                    </strong>
                    <span className="text-[#9ca3af] text-sm font-mono" data-id="element-1003">
                      Tied to real public output. Review of published work.
                    </span>
                  </div>
                </li>
              </ul>
              <p className="mt-8 font-mono text-sm text-salon-red border-l-2 border-salon-red pl-4" data-id="element-1004">
                This is a working relationship, not passive mentorship.
              </p>
            </div>

            <div data-id="element-1005">
              <h2 className="font-mono text-xs font-bold text-salon-yellow mb-2" data-id="element-1006">
                06_EXPECTATIONS
              </h2>
              <h3 className="text-3xl font-bold mb-6" data-id="element-1007">Realistic Commitment</h3>
              <div className="bg-[#f5f5f7]/10 p-6 border border-dashed border-[#9ca3af]/50 mb-6" data-id="element-1008">
                <ul className="space-y-3 text-sm font-mono" data-id="element-1009">
                  <li data-id="element-1010">• Consistent weekly effort is required</li>
                  <li data-id="element-1011">
                    • You will be expected to publish, reflect, and iterate
                  </li>
                  <li data-id="element-1012">• Progress depends on action, not attendance</li>
                  <li data-id="element-1013">• Designed to run alongside work—not replace it</li>
                </ul>
              </div>
              <div className="flex items-start gap-3 text-salon-yellow" data-id="element-1014">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" data-id="element-1015" />
                <p className="text-sm font-bold" data-id="element-1016">
                  The Drilling Pot rewards commitment. It does not accommodate
                  prolonged inactivity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLARIFICATIONS */}
      <section className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-1017">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-1018">
          <h2 className="font-mono text-xs font-bold text-[#9ca3af] mb-8" data-id="element-1019">
            07_CLARIFICATIONS
          </h2>
          <h3 className="text-3xl font-bold text-[#1f2933] mb-8" data-id="element-1020">
            To Be Clear
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left" data-id="element-1021">
            <div className="bg-red-50 p-6 border border-red-100" data-id="element-1022">
              <h4 className="font-bold text-red-800 mb-4" data-id="element-1023">What This Is NOT:</h4>
              <ul className="space-y-2 text-sm text-red-700 font-mono" data-id="element-1024">
                <li data-id="element-1025">× A certification program</li>
                <li data-id="element-1026">× A content factory</li>
                <li data-id="element-1027">× A motivational space</li>
                <li data-id="element-1028">× A shortcut to virality</li>
                <li data-id="element-1029">× A guarantee of followers or fame</li>
              </ul>
            </div>
            <div className="bg-green-50 p-6 border border-green-100" data-id="element-1030">
              <h4 className="font-bold text-green-800 mb-4" data-id="element-1031">What This IS:</h4>
              <ul className="space-y-2 text-sm text-green-700 font-mono" data-id="element-1032">
                <li data-id="element-1033">✓ Clarity</li>
                <li data-id="element-1034">✓ Structure</li>
                <li data-id="element-1035">✓ Pressure</li>
                <li data-id="element-1036">✓ Acceleration</li>
                <li data-id="element-1037">✓ Real-world execution</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* INVESTMENT SECTION */}
      <section id="payment" className="py-24 border-b border-dashed border-[#9ca3af] scroll-mt-24" data-id="element-1038">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8" data-id="element-1039">
          <div className="bg-white border-2 border-dashed border-[#1f2933] p-8 md:p-12 text-center relative" data-id="element-1040">
            <h2 className="font-mono text-xs font-bold text-salon-red mb-2" data-id="element-1041">
              08_FINANCIAL_COMMITMENT
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933] mb-8" data-id="element-1042">
              Your Investment
            </h3>

            {/* Pricing Options */}
            <div className="grid md:grid-cols-3 gap-6 mb-10" data-id="element-1043">
              <div className={`border-2 p-6 cursor-pointer transition-all ${paymentOption === 'annual' ? 'border-salon-red bg-salon-red/5' : 'border-[#9ca3af] hover:border-[#1f2933]'}`} onClick={() => setPaymentOption('annual')} data-id="element-1044">
                <div className="flex justify-between items-start mb-4" data-id="element-1045">
                  <div className="w-5 h-5 rounded-full border-2 border-[#1f2933] flex items-center justify-center" data-id="element-1046">
                    {paymentOption === 'annual' && <div className="w-3 h-3 rounded-full bg-salon-red" data-id="element-1047" />}
                  </div>
                  <Badge variant="success" className="bg-salon-red text-white border-none" data-id="element-1048">
                    PREFERRED
                  </Badge>
                </div>
                <h4 className="text-xl font-bold text-[#1f2933] mb-2" data-id="element-1049">
                  Annual Commitment
                </h4>
                <div className="flex flex-col items-center mb-2" data-id="element-1050">
                  <span className="text-lg text-[#9ca3af] line-through font-mono" data-id="element-1051">
                    ₦1,200,000
                  </span>
                  <span className="text-3xl font-bold text-[#1f2933]" data-id="element-1052">
                    ₦700,000
                  </span>
                </div>
                <p className="text-xs text-[#9ca3af] font-mono" data-id="element-1053">
                  Paid Upfront • Signals Seriousness
                </p>
              </div>

              <div className={`border-2 p-6 cursor-pointer transition-all ${paymentOption === 'split' ? 'border-salon-red bg-salon-red/5' : 'border-[#9ca3af] hover:border-[#1f2933]'}`} onClick={() => setPaymentOption('split')} data-id="element-1054">
                <div className="flex justify-between items-start mb-4" data-id="element-1055">
                  <div className="w-5 h-5 rounded-full border-2 border-[#1f2933] flex items-center justify-center" data-id="element-1056">
                    {paymentOption === 'split' && <div className="w-3 h-3 rounded-full bg-salon-red" data-id="element-1057" />}
                  </div>
                  <Badge variant="success" className="bg-salon-teal text-white border-none" data-id="element-1058">
                    FLEXIBLE
                  </Badge>
                </div>
                <h4 className="text-xl font-bold text-[#1f2933] mb-2" data-id="element-1059">
                  Split Payment
                </h4>
                <div className="flex flex-col items-center mb-2" data-id="element-1060">
                  <span className="text-3xl font-bold text-[#1f2933]" data-id="element-1061">
                    ₦350,000
                  </span>
                  <span className="text-xs text-[#1f2933] font-mono" data-id="element-1062">
                    × 2 payments
                  </span>
                </div>
                <p className="text-xs text-[#9ca3af] font-mono" data-id="element-1063">
                  50% Now • 50% Month 2
                </p>
              </div>

              <div className={`border-2 p-6 cursor-pointer transition-all ${paymentOption === 'quarterly' ? 'border-salon-red bg-salon-red/5' : 'border-[#9ca3af] hover:border-[#1f2933]'}`} onClick={() => setPaymentOption('quarterly')} data-id="element-1064">
                <div className="flex justify-between items-start mb-4" data-id="element-1065">
                  <div className="w-5 h-5 rounded-full border-2 border-[#1f2933] flex items-center justify-center" data-id="element-1066">
                    {paymentOption === 'quarterly' && <div className="w-3 h-3 rounded-full bg-salon-red" data-id="element-1067" />}
                  </div>
                </div>
                <h4 className="text-xl font-bold text-[#1f2933] mb-2" data-id="element-1068">
                  Quarterly Commitment
                </h4>
                <div className="flex flex-col items-center mb-2" data-id="element-1069">
                  <span className="text-3xl font-bold text-[#1f2933]" data-id="element-1070">
                    ₦350,000
                  </span>
                  <span className="text-xs text-[#1f2933] font-mono" data-id="element-1071">
                    per quarter
                  </span>
                </div>
                <p className="text-xs text-[#9ca3af] font-mono" data-id="element-1072">
                  Total: ₦1,400,000 • 4 Payments
                </p>
              </div>
            </div>

            <div className="bg-[#f5f5f7] p-6 mb-8 text-left text-sm font-mono text-[#1f2933]/80" data-id="element-1073">
              <p className="mb-2 font-bold" data-id="element-1074">Payment confirms:</p>
              <ul className="space-y-1 mb-4" data-id="element-1075">
                <li data-id="element-1076">• Commitment to the full year</li>
                <li data-id="element-1077">• Availability to do the work</li>
                <li data-id="element-1078">• Understanding that this is a high-trust engagement</li>
              </ul>
              {paymentOption === 'split' && <div className="mt-4 pt-4 border-t border-dashed border-[#9ca3af]" data-id="element-1079">
                  <p className="text-salon-red font-bold mb-2" data-id="element-1080">
                    Split Payment Schedule:
                  </p>
                  <ul className="space-y-1" data-id="element-1081">
                    <li data-id="element-1082">• First payment: ₦350,000 (today)</li>
                    <li data-id="element-1083">• Second payment: ₦350,000 (beginning of month 2)</li>
                    <li data-id="element-1084">
                      • You will be contacted with payment details for the
                      second installment
                    </li>
                  </ul>
                </div>}
              <p className="text-xs text-[#9ca3af] mt-4" data-id="element-1085">
                Fees cover time, judgment, experience, and sustained access—not
                content volume.
              </p>
            </div>

            {/* Payment Form */}
            {showForm && <div className="mb-6 space-y-4 max-w-md mx-auto text-left" data-id="element-1086">
                <div data-id="element-1087">
                  <label className="block text-xs font-mono text-[#9ca3af] mb-1" data-id="element-1088">
                    FULL_NAME
                  </label>
                  <input type="text" placeholder="Enter your full name" value={name} onChange={e => setName(e.target.value)} className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-salon-red focus:outline-none" required data-id="element-1089" />
                </div>
                <div data-id="element-1090">
                  <label className="block text-xs font-mono text-[#9ca3af] mb-1" data-id="element-1091">
                    EMAIL_ADDRESS
                  </label>
                  <input type="email" placeholder="Enter your email address" value={email} onChange={e => setEmail(e.target.value)} className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-salon-red focus:outline-none" required data-id="element-1092" />
                </div>
              </div>}

            <Button size="large" onClick={handlePayment} className="w-full max-w-md bg-salon-red hover:bg-salon-red/90 border-salon-red text-white" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-1094" />} data-id="element-1093">
              SUBMIT_APPLICATION
            </Button>

            <p className="font-mono text-xs text-[#9ca3af] mt-6" data-id="element-1095">
              Secure payment via Paystack • Application fee is non-refundable
            </p>
          </div>
        </div>
      </section>

      {/* ENTRY PROCESS */}
      <section className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-1096">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-1097">
          <h2 className="font-mono text-xs font-bold text-[#1f2933] mb-2" data-id="element-1098">
            09_APPLICATION
          </h2>
          <h3 className="text-2xl font-bold text-[#1f2933] mb-6" data-id="element-1099">
            By Application Only
          </h3>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-sm font-mono text-[#1f2933]/80" data-id="element-1100">
            <div className="flex items-center gap-2" data-id="element-1101">
              <span className="w-6 h-6 rounded-full bg-[#1f2933] text-white flex items-center justify-center text-xs" data-id="element-1102">
                1
              </span>
              <span data-id="element-1103">Alignment Conversation</span>
            </div>
            <div className="hidden md:block w-8 h-px bg-[#9ca3af]" data-id="element-1104"></div>
            <div className="flex items-center gap-2" data-id="element-1105">
              <span className="w-6 h-6 rounded-full bg-[#1f2933] text-white flex items-center justify-center text-xs" data-id="element-1106">
                2
              </span>
              <span data-id="element-1107">Confirm Fit</span>
            </div>
            <div className="hidden md:block w-8 h-px bg-[#9ca3af]" data-id="element-1108"></div>
            <div className="flex items-center gap-2" data-id="element-1109">
              <span className="w-6 h-6 rounded-full bg-[#1f2933] text-white flex items-center justify-center text-xs" data-id="element-1110">
                3
              </span>
              <span data-id="element-1111">Begin Apprenticeship</span>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-32 text-center bg-[#1f2933] text-white" data-id="element-1112">
        <div className="mx-auto max-w-4xl px-4" data-id="element-1113">
          <h2 className="text-4xl font-bold mb-8 tracking-tight" data-id="element-1114">
            The Drilling Pot is for builders.
          </h2>

          <div className="flex flex-wrap justify-center gap-6 mb-12 font-mono text-sm text-[#9ca3af]" data-id="element-1115">
            <span data-id="element-1116">THINK CLEARLY</span>
            <span data-id="element-1117">ACT CONSISTENTLY</span>
            <span data-id="element-1118">LEARN IN PUBLIC</span>
            <span data-id="element-1119">GROW DELIBERATELY</span>
          </div>

          <p className="text-lg text-[#f5f5f7]/80 mb-12 italic" data-id="element-1120">
            "If that resonates, the next step is a conversation—not persuasion."
          </p>

          <Button size="large" className="min-w-[250px] bg-salon-red hover:bg-white hover:text-[#1f2933] border-salon-red text-white" onClick={scrollToPayment} data-id="element-1121">
            BEGIN_APPLICATION
          </Button>
        </div>
      </section>

      <Footer onNavigate={onNavigate} data-id="element-1122" />
    </div>;
}